import { useState, useEffect } from 'react';
import { useAuth } from '@/lib/AuthContext';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PROVINCES } from '@/lib/cartStore';
import { toast } from 'sonner';
import { Loader2, User, KeyRound } from 'lucide-react';
import { MfaSettings } from '@/components/MfaSettings';

export default function MyAccount() {
  const { user, updateProfile } = useAuth();
  const [form, setForm] = useState({ full_name: '', phone: '', delivery_province: '', delivery_municipality: '' });
  const [saving, setSaving] = useState(false);
  const [pwd, setPwd] = useState({ current_password: '', new_password: '', confirm: '' });
  const [pwdSaving, setPwdSaving] = useState(false);

  useEffect(() => { if (user) setForm({ full_name: user.full_name || '', phone: user.phone || '', delivery_province: user.delivery_province || '', delivery_municipality: user.delivery_municipality || '' }); }, [user]);

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await updateProfile(form); toast.success('Perfil actualizado'); }
    catch { toast.error('Error al guardar'); }
    finally { setSaving(false); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwd.new_password.length < 8) { toast.error('La nueva contraseña debe tener al menos 8 caracteres'); return; }
    if (pwd.new_password !== pwd.confirm) { toast.error('Las contraseñas no coinciden'); return; }
    setPwdSaving(true);
    try {
      await api.post('/auth/change-password', { current_password: pwd.current_password, new_password: pwd.new_password });
      toast.success('Contraseña actualizada');
      setPwd({ current_password: '', new_password: '', confirm: '' });
    } catch (err) { toast.error(err.response?.data?.detail || 'No se pudo actualizar la contraseña'); }
    finally { setPwdSaving(false); }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <div className="flex items-center gap-3 mb-6"><div className="w-12 h-12 rounded-full bg-[#1a3a5c] flex items-center justify-center"><User className="h-6 w-6 text-[#e0a52a]" /></div><div><h1 className="font-heading font-bold text-2xl text-foreground">Mi Cuenta</h1><p className="text-sm text-muted-foreground">{user?.email}</p></div></div>
      <form onSubmit={save} className="bg-card rounded-xl border border-border p-6 space-y-4">
        <div><Label className="text-xs">Nombre completo</Label><Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="acc-name" className="mt-1" /></div>
        <div><Label className="text-xs">Teléfono</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="acc-phone" className="mt-1" /></div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label className="text-xs">Provincia de entrega</Label>
            <Select value={form.delivery_province} onValueChange={(v) => setForm({ ...form, delivery_province: v })}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
              <SelectContent>{PROVINCES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Municipio</Label><Input value={form.delivery_municipality} onChange={(e) => setForm({ ...form, delivery_municipality: e.target.value })} className="mt-1" /></div>
        </div>
        <Button type="submit" disabled={saving} data-testid="acc-save" className="bg-[#1a3a5c] hover:bg-[#1a3a5c]/90 rounded-full">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Guardar cambios'}</Button>
      </form>

      <form onSubmit={changePassword} className="bg-card rounded-xl border border-border p-6 space-y-4 mt-6" data-testid="acc-password-card">
        <div className="flex items-center gap-2"><KeyRound className="h-5 w-5 text-brand-navy" /><h2 className="font-body font-bold text-lg text-foreground">Cambiar contraseña</h2></div>
        <p className="text-xs text-muted-foreground">Solo para cuentas con contraseña. Las cuentas de Google se gestionan desde tu proveedor.</p>
        <div><Label className="text-xs">Contraseña actual</Label><Input type="password" autoComplete="current-password" required value={pwd.current_password} onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })} data-testid="acc-current-password" className="mt-1" /></div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label className="text-xs">Nueva contraseña (mín. 8)</Label><Input type="password" autoComplete="new-password" minLength={8} required value={pwd.new_password} onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })} data-testid="acc-new-password" className="mt-1" /></div>
          <div><Label className="text-xs">Confirmar nueva</Label><Input type="password" autoComplete="new-password" minLength={8} required value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} data-testid="acc-confirm-password" className="mt-1" /></div>
        </div>
        <Button type="submit" disabled={pwdSaving} data-testid="acc-password-save" className="bg-[#1a3a5c] hover:bg-[#1a3a5c]/90 rounded-full">{pwdSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Actualizar contraseña'}</Button>
      </form>
      <MfaSettings />
    </div>
  );
}
